// Operator identity — the IP the backend observes for *this* session, so the UI
// can flag "this is your IP" next to matching addresses (and warn loudly before
// banning your own IP). Loaded once on entry via GET /api/v1/whoami.
//
// `.svelte.ts` so the `$state` is reactive: reading `isMyIp(ip)` inside a
// template / `$derived` tracks `_ip`, so badges light up the moment whoami
// resolves.

import { auth } from './api';

interface Geo {
	country_code?: string;
	country_name?: string;
	city?: string;
}

let _ip = $state<string | null>(null);
let _geo = $state<Geo>({});
let _attempted = false;
let _inflight = false;

export const identity = {
	get ip(): string | null {
		return _ip;
	},
	get geo(): Geo {
		return _geo;
	},
};

/** True when `ip` is the operator's own current IP. */
export function isMyIp(ip: string | null | undefined): boolean {
	return !!ip && _ip !== null && ip === _ip;
}

/**
 * Fetch the operator's IP once. Safe to call from multiple entry points
 * (app mount, post-login, route changes) — it no-ops once loaded and never
 * throws (a 401 / network error just leaves the badge off).
 */
export async function loadIdentity(force = false): Promise<void> {
	if (_inflight) return;
	if (_attempted && _ip && !force) return;
	_inflight = true;
	try {
		const r = await auth.whoami();
		_ip = r.ip || null;
		_geo = { country_code: r.country_code, country_name: r.country_name, city: r.city };
		_attempted = true;
	} catch {
		// Not logged in yet / API unreachable — leave identity unset.
	} finally {
		_inflight = false;
	}
}

/** Clear identity (on logout) so the next session re-resolves. */
export function resetIdentity(): void {
	_ip = null;
	_geo = {};
	_attempted = false;
}
